// The append-only log, rendered. Phrased in our words ("Travis logged a call")
// rather than event names, because the audit trail is for people first — the raw
// `entry.create` sits underneath in the metadata voice for when you want it.

import { useMemo, useState, type ReactNode } from 'react'

import { AgentAvatar, Avatar, EmptyState } from '../components/common'
import { EntityLink } from '../components/EntityLink'
import { View } from '../components/Page'
import { TextMenu } from '../components/Picker'
import { groupRuns, type Run } from '../lib/activity'
import type { CrumEvent } from '../lib/events'
import { formatDate, relativeTime } from '../lib/format'
import { entryKind, itemStatus, KINDS, kindOfId, labelOf, VISIBILITY_LABELS } from '../lib/model'
import type { State } from '../lib/reducer'
import { titleOf } from '../lib/reducer'
import { useStore } from '../lib/store'

function verbFor(singular: string): Record<string, string> {
  const s = singular.toLowerCase()
  return {
    create: `created ${s}`,
    update: `updated ${s}`,
    archive: `archived ${s}`,
    restore: `restored ${s}`,
  }
}

export function describe(ev: CrumEvent, state: State): ReactNode {
  const [entity, verb = ''] = ev.type.split('.')
  const kind = kindOfId(ev.subject)
  const data = ev.data ?? {}

  if (
    kind &&
    (entity === 'project' || entity === 'deal' || entity === 'org' || entity === 'person')
  ) {
    if (verb === 'stage_change') {
      return (
        <>
          moved <EntityLink id={ev.subject} /> to <b>{String(data.to ?? '')}</b>
        </>
      )
    }
    const phrase =
      verbFor(KINDS[kind].singular)[verb] ?? `${verb} ${KINDS[kind].singular.toLowerCase()}`
    return (
      <>
        {phrase} <EntityLink id={ev.subject} />
      </>
    )
  }

  // `note.*` is v1's vocabulary for the same record; both fold into entries.
  if (entity === 'entry' || entity === 'note') {
    const rec = state.entries[ev.subject]
    const subject = rec?.subject ?? String(data.subject ?? data.target ?? '')
    const kindLabel = entryKind(rec?.kind ?? String(data.kind ?? 'note')).verb
    if (verb === 'create') {
      return (
        <>
          {kindLabel} on {subject ? <EntityLink id={subject} /> : 'a record'}
        </>
      )
    }
    return <>{verb === 'archive' ? 'archived an entry' : 'edited an entry'}</>
  }
  if (entity === 'comment') {
    const entryId = state.comments[ev.subject]?.entry ?? String(data.entry ?? data.note ?? '')
    const subject = entryId ? state.entries[entryId]?.subject : undefined
    return <>replied on {subject ? <EntityLink id={subject} /> : 'an entry'}</>
  }
  if (entity === 'commitment') {
    const rec = state.commitments[ev.subject]
    const label = rec?.label ?? String(data.label ?? 'a commitment')
    const deal = rec?.deal ?? String(data.deal ?? '')
    if (verb === 'create')
      return (
        <>
          promised <b>{label}</b>
          {deal ? (
            <>
              {' '}
              on <EntityLink id={deal} />
            </>
          ) : null}
        </>
      )
    if (verb === 'update' && data.deliveredAt)
      return (
        <>
          delivered <b>{label}</b>
        </>
      )
    if (verb === 'update' && 'deliveredAt' in data)
      return (
        <>
          reopened <b>{label}</b>
        </>
      )
    return (
      <>
        {verb === 'archive' ? 'dropped' : 'updated'} <b>{label}</b>
      </>
    )
  }
  // `showing` and `resource` events exist only in older logs — both record types were
  // removed. They still fold (as nothing) and still read here, in plain words.
  if (entity === 'showing' || entity === 'resource') {
    return (
      <>
        {verb === 'create' ? 'added' : verb === 'archive' ? 'removed' : 'edited'} a{' '}
        {entity === 'showing' ? 'showing' : 'resource'} <i>(a record type since retired)</i>
      </>
    )
  }
  if (entity === 'support') {
    const project = String(data.project ?? '')
    return (
      <>
        {verb === 'add' ? 'attached' : 'detached'}{' '}
        {project ? <EntityLink id={project} /> : 'some work'} {verb === 'add' ? 'to' : 'from'}{' '}
        <EntityLink id={ev.subject} />
      </>
    )
  }
  if (entity === 'tag') {
    return (
      <>
        {verb === 'add' ? 'tagged' : 'untagged'} <EntityLink id={ev.subject} />{' '}
        <b>#{String(data.label ?? '')}</b>
      </>
    )
  }
  if (entity === 'link') {
    return (
      <>
        linked <EntityLink id={ev.subject} /> ↔ <EntityLink id={String(data.to ?? '')} />
      </>
    )
  }
  if (entity === 'group') {
    const rec = state.groups[ev.subject]
    const label = String(data.label ?? '') || rec?.label || 'a bucket'
    const where = boardLink(state, rec?.work || String(data.work ?? ''))
    if (verb === 'create')
      return (
        <>
          added the bucket <b>{label}</b>
          {where}
        </>
      )
    if (verb === 'archive')
      return (
        <>
          removed the bucket <b>{label}</b>
          {where}
        </>
      )
    if (verb === 'restore')
      return (
        <>
          restored the bucket <b>{label}</b>
          {where}
        </>
      )
    if ('label' in data)
      return (
        <>
          renamed a bucket to <b>{label}</b>
          {where}
        </>
      )
    if ('due' in data)
      return (
        <>
          {data.due ? 'dated' : 'cleared the date on'} <b>{label}</b>
          {where}
        </>
      )
    if ('order' in data)
      return (
        <>
          reordered <b>{label}</b>
          {where}
        </>
      )
    return (
      <>
        updated the bucket <b>{label}</b>
        {where}
      </>
    )
  }
  if (entity === 'item') {
    const rec = state.items[ev.subject]
    // The log's own word wins. `data.label` is what was written at the time; the row
    // may have been renamed since, and a feed that shows today's name for a year-old
    // line is quietly rewriting history.
    const label = String(data.label ?? '') || rec?.label || 'a row'
    const where = boardLink(state, workOfItem(state, ev, data))
    if (verb === 'create')
      return (
        <>
          added <b>{label}</b>
          {where}
        </>
      )
    if (verb === 'archive')
      return (
        <>
          removed <b>{label}</b>
          {where}
        </>
      )
    if (verb === 'restore')
      return (
        <>
          put <b>{label}</b> back
          {where}
        </>
      )
    // An update names the FIELD, because "updated a row" is the line that made this
    // feed useless: every board edit looked identical and none of them said what
    // changed. Ordered by how often each one is the interesting one.
    if ('status' in data)
      return (
        <>
          moved <b>{label}</b> to <b>{itemStatus(String(data.status ?? '')).label}</b>
          {where}
        </>
      )
    if ('owner' in data)
      return data.owner ? (
        <>
          gave <b>{label}</b> to <EntityLink id={String(data.owner)} />
        </>
      ) : (
        <>
          took the owner off <b>{label}</b>
        </>
      )
    if ('due' in data)
      return (
        <>
          {data.due ? (
            <>
              dated <b>{label}</b> {formatDate(String(data.due))}
            </>
          ) : (
            <>
              cleared the date on <b>{label}</b>
            </>
          )}
        </>
      )
    if ('group' in data || 'work' in data) {
      const bucket = state.groups[String(data.group ?? '')]
      return (
        <>
          moved <b>{label}</b>{' '}
          {bucket ? (
            <>
              into <b>{bucket.label}</b>
            </>
          ) : (
            'out of its bucket'
          )}
          {where}
        </>
      )
    }
    if ('note' in data)
      return (
        <>
          wrote on <b>{label}</b>
          {where}
        </>
      )
    if ('waitingOn' in data)
      return (
        <>
          set what <b>{label}</b> waits on
          {where}
        </>
      )
    if ('clientPriority' in data)
      return (
        <>
          ranked <b>{label}</b>
          {where}
        </>
      )
    if ('label' in data)
      return (
        <>
          renamed a row to <b>{label}</b>
          {where}
        </>
      )
    if ('visibility' in data)
      return (
        <>
          set <b>{label}</b> to{' '}
          <b>
            {labelOf(VISIBILITY_LABELS, String(data.visibility ?? ''))?.label ?? 'a visibility'}
          </b>
          {where}
        </>
      )
    return (
      <>
        updated <b>{label}</b>
        {where}
      </>
    )
  }
  return <>{ev.type}</>
}

/**
 * Which board a row belongs to.
 *
 * Three places to look and all three are load-bearing: the folded record, the event
 * that is setting it, and — for legacy rows written before an item carried its own
 * `work` — the bucket it sits in.
 */
export function workOfItem(state: State, ev: CrumEvent, data: Record<string, unknown>): string {
  const rec = state.items[ev.subject]
  const direct = rec?.work || String(data.work ?? '')
  if (direct) return direct
  const group = rec?.group || String(data.group ?? '')
  return group ? (state.groups[group]?.work ?? '') : ''
}

/** " on <the board>", or nothing when the board is gone or was never named. */
function boardLink(state: State, work: string): ReactNode {
  if (!work || !state.entities[work]) return null
  return (
    <>
      {' '}
      on <EntityLink id={work} />
    </>
  )
}

/**
 * Where an event happened, as a phrase.
 *
 * A board row happens ON a board; an entry happens ON the record it is filed
 * against; everything else happens TO the record it names. The preposition is part
 * of the sentence, so it lives with the link rather than being glued on at each
 * call site.
 */
function placeOf(state: State, ev: CrumEvent): { id: string; on: boolean } {
  const [entity] = ev.type.split('.')
  const data = ev.data ?? {}
  if (entity === 'item') return { id: workOfItem(state, ev, data), on: true }
  if (entity === 'group')
    return { id: state.groups[ev.subject]?.work || String(data.work ?? ''), on: true }
  if (entity === 'entry' || entity === 'note')
    return {
      id: state.entries[ev.subject]?.subject || String(data.subject ?? data.target ?? ''),
      on: true,
    }
  if (entity === 'comment') {
    const entryId = state.comments[ev.subject]?.entry ?? String(data.entry ?? data.note ?? '')
    return { id: entryId ? (state.entries[entryId]?.subject ?? '') : '', on: true }
  }
  return { id: ev.subject, on: false }
}

function placeNode(state: State, ev: CrumEvent): ReactNode {
  const { id, on } = placeOf(state, ev)
  if (!id || !state.entities[id]) return null
  return (
    <>
      {' '}
      {on ? 'on' : 'to'} <EntityLink id={id} />
    </>
  )
}

/**
 * A folded run, in one sentence.
 *
 * The count is the point — "added 12 rows on Cairn" is the act, and the twelve
 * lines it fell out as are the receipt, which is what opening the run shows. A run
 * whose shape has no phrase here still says how many and where, which is already
 * more than twelve copies of the event name.
 */
export function describeRun(run: Run, state: State): ReactNode {
  const newest = run.events[0]!
  if (run.events.length === 1) return describe(newest, state)

  const n = run.events.length
  const [entity, verb = ''] = newest.type.split('.')
  const where = placeNode(state, newest)
  const count = <b>{n}</b>

  if (entity === 'item') {
    if (verb === 'create')
      return (
        <>
          added {count} rows{where}
        </>
      )
    if (verb === 'archive')
      return (
        <>
          removed {count} rows{where}
        </>
      )
    if (verb === 'update') {
      // A whole run moved to the same column is the one board act worth naming.
      const first = String(newest.data?.status ?? '')
      const same =
        first && run.events.every((e) => String(e.data?.status ?? '') === first) ? first : ''
      if (same)
        return (
          <>
            moved {count} rows to <b>{itemStatus(same).label}</b>
            {where}
          </>
        )
      return (
        <>
          changed {count} rows{where}
        </>
      )
    }
  }
  if (entity === 'group' && verb === 'create')
    return (
      <>
        added {count} buckets{where}
      </>
    )
  if ((entity === 'entry' || entity === 'note') && verb === 'create')
    return (
      <>
        wrote {count} entries{where}
      </>
    )
  if (entity === 'link')
    return (
      <>
        made {count} links{where}
      </>
    )
  if (entity === 'tag')
    return (
      <>
        {verb === 'add' ? 'tagged' : 'untagged'} {count} times{where}
      </>
    )
  if (entity === 'support')
    return (
      <>
        {verb === 'add' ? 'attached' : 'detached'} {count} projects{where}
      </>
    )
  return (
    <>
      made {count} changes{where}
    </>
  )
}

/** What the feed can be sliced by. Deliberately three, and each answers a
 *  question someone actually asks — not one filter per field. */
const SLICES = [
  { key: 'all', label: 'Everything' },
  {
    key: 'people',
    label: 'What people wrote',
    test: (t: string) => t.startsWith('entry.') || t.startsWith('comment.'),
  },
  { key: 'money', label: 'Deals moving', test: (t: string) => t.startsWith('deal.') },
  {
    key: 'work',
    label: 'Work and boards',
    test: (t: string) =>
      t.startsWith('project.') || t.startsWith('group.') || t.startsWith('item.'),
  },
] as const

/**
 * How many matching events the page holds at once.
 *
 * Folding means this is a lot more than 300 LINES was: a board seed is three
 * hundred events and four lines. The scan that fills it walks the log backwards
 * and stops as soon as it has enough, so the cost is the window, not the log.
 */
const WINDOW = 1000

/**
 * Activity, grouped by day and folded into runs.
 *
 * A flat reverse-chronological list is a log, and a log is only readable when you
 * already know what you are looking for. Days are the grouping that makes it
 * skimmable — "what happened Tuesday" is the question people actually have — and
 * the slices answer the other one, which is "what happened to X kind of thing".
 *
 * Runs are the second fold, and they are what makes the page readable at all once
 * boards are in use: twelve rows added to a board is one act, and it used to print
 * as twelve identical lines that said `item.create` and nothing else. See
 * `lib/activity.ts` for the rules a run obeys.
 *
 * This took over from a separate Overview tab of rollup numbers. Those numbers
 * were all derivable from the lists they linked to, and a dashboard that only
 * points elsewhere is a stop on the way to somewhere.
 */
export function ActivityFeed() {
  const { state, version } = useStore()
  const [slice, setSlice] = useState<string>('all')
  const [who, setWho] = useState<string>('')
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set())

  // Everyone who has actually written something, so the filter never offers a
  // name with nothing behind it. One pass, and only when the log changes — it used
  // to build a Set over every event on every render, filter included.
  const actors = useMemo(() => {
    void version
    const names = new Set<string>()
    for (const ev of state.events) names.add(ev.actor?.name ?? 'unknown')
    return [...names].sort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  const days = useMemo(() => {
    void version
    const def = SLICES.find((s) => s.key === slice)
    const test = def && 'test' in def ? def.test : null

    // Backwards from the end, stopping at WINDOW. The old version copied the whole
    // array, reversed the copy, filtered it twice and then threw all but 300 away —
    // four passes over the entire log for one screen, on every render.
    const picked: CrumEvent[] = []
    for (let i = state.events.length - 1; i >= 0 && picked.length < WINDOW; i--) {
      const ev = state.events[i]!
      if (test && !test(ev.type)) continue
      if (who && (ev.actor?.name ?? 'unknown') !== who) continue
      picked.push(ev)
    }

    // Days first, runs inside a day: a run that spanned midnight would have to be
    // drawn under one date or the other, and either one is a lie.
    const byDay = new Map<string, CrumEvent[]>()
    for (const ev of picked) {
      const day = ev.ts.slice(0, 10)
      const list = byDay.get(day)
      if (list) list.push(ev)
      else byDay.set(day, [ev])
    }
    // **Sorted by date, not by the order the log put them in.** Those are the same
    // thing only while every event is written when it happened, and an import or a
    // backdated entry breaks it: the page was printing September, then January,
    // then September again, because it kept whatever order the events arrived in.
    // The day heading is read off `ts`, so `ts` has to be what orders it — inside a
    // day too, or a 9am change sits above a 5pm one under the same heading.
    return [...byDay.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
      .map(([day, list]) => {
        const inOrder = [...list].sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0))
        return {
          day,
          changes: inOrder.length,
          runs: groupRuns(inOrder, (ev) => placeOf(state, ev).id),
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, slice, who])

  const shown = days.reduce((n, d) => n + d.changes, 0)

  return (
    // The slices are LENSES — they narrow what this page shows without changing
    // what it is — so they are the app's lens chips in the app's view bar, not the
    // fourth chip shape this page used to draw for itself inside the content.
    <View
      lenses={SLICES.map((s) => ({ key: s.key, label: s.label }))}
      active={slice}
      onLens={setSlice}
      actions={
        actors.length > 1 ? (
          <TextMenu
            label="Filter by who"
            chevron
            value={who}
            empty="Anyone"
            onPick={setWho}
            options={[{ key: '', label: 'Anyone' }, ...actors.map((a) => ({ key: a, label: a }))]}
          />
        ) : null
      }
    >
      {days.length === 0 ? (
        <EmptyState
          icon="Activity"
          title="Nothing here"
          hint="Every change, with who made it and when."
        />
      ) : (
        <>
          {days.map(({ day, changes, runs }) => (
            <section key={day} className="mb-5">
              <div
                className="mb-1 flex items-baseline gap-2.5 border-b pb-1"
                style={{ borderColor: 'var(--border)' }}
              >
                <span className="text-body font-semibold">{formatDate(day)}</span>
                <span className="meta">
                  {changes} {changes === 1 ? 'change' : 'changes'}
                  {runs.length < changes ? ` · ${runs.length} lines` : ''}
                </span>
              </div>
              <div className="rows">
                {runs.map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    state={state}
                    open={open.has(run.id)}
                    onToggle={() =>
                      setOpen((prev) => {
                        const next = new Set(prev)
                        if (next.has(run.id)) next.delete(run.id)
                        else next.add(run.id)
                        return next
                      })
                    }
                  />
                ))}
              </div>
            </section>
          ))}
          {shown >= WINDOW && (
            <p className="meta pb-6">
              The most recent {WINDOW} changes. The whole log is on the server — export it from
              Settings for anything older.
            </p>
          )}
        </>
      )}
    </View>
  )
}

/** One line: a single change, or a run you can open. */
function RunRow({
  run,
  state,
  open,
  onToggle,
}: {
  run: Run
  state: State
  open: boolean
  onToggle: () => void
}) {
  const newest = run.events[0]!
  const folded = run.events.length > 1
  const agent = isAgent(state, newest)

  return (
    // One line, not two. This was the least dense screen in the app — about 48px a
    // row to carry one sentence, against 40px rows carrying six columns in the
    // lists — because the actor, the verb and the stamp each took their own line.
    <div className="text-body py-1.5">
      <div className="flex items-baseline gap-2.5">
        <span className="shrink-0 self-start pt-0.5">
          {agent ? <AgentAvatar size={16} /> : <Avatar actor={newest.actor} size={16} />}
        </span>
        <div className="min-w-0 flex-1 leading-snug">
          <span className="font-medium">
            {agent ? 'An agent' : (newest.actor?.name ?? 'unknown')}
          </span>{' '}
          {describeRun(run, state)}
          <span className="meta ml-1.5 inline-flex flex-wrap items-baseline gap-x-1.5">
            {/* `newest.type` — `entry.create`, `deal.update` — used to print on every
                row. That is the event name out of the log: developer-facing text on
                a screen a client-facing product shows. It is in the title now, where
                it is still there when you are debugging and nowhere when you are not. */}
            <span title={newest.type}>
              {relativeTime(newest.ts)}
              {!folded && newest.subject && state.entities[newest.subject]
                ? ` · ${titleOf(state, newest.subject)}`
                : ''}
            </span>
            {folded && (
              // The receipt for the fold. Not a chevron on its own: the count is the
              // thing you want to see without opening anything.
              <button
                type="button"
                onClick={onToggle}
                className="rounded-inline px-1"
                style={{ color: 'var(--marine)' }}
                aria-expanded={open}
              >
                {open ? 'hide' : `show all ${run.events.length}`}
              </button>
            )}
          </span>
        </div>
      </div>

      {folded && open && (
        <div className="mt-1.5 ml-[31px] border-l pl-3" style={{ borderColor: 'var(--border)' }}>
          {run.events.map((ev) => (
            <div key={ev.id} className="py-1 leading-snug">
              {describe(ev, state)}{' '}
              <span className="meta" title={ev.type}>
                {relativeTime(ev.ts)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** An entry written by an agent seat wears the agent's face, not a person's. */
function isAgent(state: State, ev: CrumEvent): boolean {
  return ev.type.startsWith('entry.') && state.entries[ev.subject]?.source === 'agent'
}
